import React, { FormHTMLAttributes, useState } from 'react'
import { useFormik } from 'formik'
import parse from 'html-react-parser'

import ButtonMask from '@/components/base/button-mask'
import FormControl from '@/components/base/form-control'
import FormSubmit from '@/components/base/form-submit'
import Typography from '@/components/base/typography'
import { convertJsontoFormData, createInitialValues, createYupSchema } from '@/utils/form'
import { API } from '@/constants'
import { axiosServer as axios } from '@/base/axios'

import { cn } from '@/lib/utils'
import { WPCF7Form } from '@/types'

export type FormProps = FormHTMLAttributes<HTMLFormElement> &
  Partial<{
    form_id: string
    submit: string
    fields: WPCF7Form['form']
  }>

const Form = ({ form_id, fields = [], submit, className, ...props }: FormProps) => {
  const [statusMessage, setStatusMessage] = useState('&nbsp;')
  const [selectResetKeys, setSelectResetKeys] = useState<{ [key: string]: number }>({})

  async function sendContactForm(sendData: any) {
    const headers = {
      'Content-Type': 'multipart/form-data'
    }
    const response = await axios.post(
      `${API.GRAVITY_FORM}/${form_id}/submissions`,
      convertJsontoFormData(sendData),
      { headers }
    )
    return response
  }

  const formik = useFormik<any>({
    initialValues: createInitialValues(fields) || {},
    validationSchema: createYupSchema(fields, 'en'),
    enableReinitialize: true,
    onSubmit: (values, { setStatus, setSubmitting, resetForm }) => {
      const result = sendContactForm({
        ...values
      })
      result
        .then((response) => {
          setStatusMessage(response.data.confirmation_message)
          setStatus(response.data.is_valid ? 'success' : 'error')
        })
        .then(() => {
          setTimeout(() => {
            // Genera nuove chiavi di reset per le select
            const newResetKeys = fields.reduce((acc: { [key: string]: number }, field) => {
              if (field.type === 'select') {
                acc[`input_${field.id}`] = (selectResetKeys[`input_${field.id}`] || 0) + 1
              }
              return acc
            }, {})

            // Aggiorna le chiavi di reset
            setSelectResetKeys(newResetKeys)

            resetForm()
            setStatusMessage('&nbsp;')
            setStatus('')
            setSubmitting(false)
          }, 3000)
        })
    }
  })

  return (
    <form
      noValidate
      autoComplete="off"
      onSubmit={formik.handleSubmit}
      className={cn([
        'grid grid-cols-2 gap-x-gutter gap-y-[2rem] md:grid-cols-4 lg:grid-cols-9',
        className
      ])}
      {...props}
    >
      {fields?.map((field, index) => {
        return (
          <FormControl
            key={index}
            resetKey={selectResetKeys[`input_${field.id}`]?.toString()}
            id={`input_${field.id}`}
            name={`input_${field.id}`}
            type={field.type}
            className={cn([
              'col-span-2 md:col-span-4 lg:col-span-9',
              {
                'md:col-span-2 lg:col-span-3': field.cssClass === 'half',
                'lg:col-span-7': field.type === 'consent'
                // 'mt-[1rem]': field.type === 'consent'
              }
            ])}
            label={field.type === 'consent' ? field.checkboxLabel : field.label}
            required={field.isRequired}
            choices={field.choices}
            placeholder={`${field.placeholder || field.label}${field.isRequired ? ' *' : ''}`}
            checked={formik.values[`input_${field.id}`] || false}
            value={formik.values[`input_${field.id}`]}
            disabled={formik.isSubmitting}
            onChange={(e: any) => {
              if (field.type === 'consent') {
                formik.handleChange(e)
                formik.setFieldValue(`input_${field.id}_1`, e.target.checked ? '1' : '0', false)
              } else if (field.type === 'select') {
                formik.setFieldValue(`input_${field.id}`, e, false)
              } else if (field.type === 'checkbox' || field.type === 'radio') {
                formik.setFieldValue(`input_${field.id}`, e, true)
              } else if (field.type === 'multi_choice') {
                const selected: string[] = Array.isArray(e) ? e : []
                formik.setFieldValue(`input_${field.id}`, selected, true)
                field.choices?.forEach((choice, i) => {
                  const key = `input_${field.id}_${i + 1}`
                  if (selected.includes(choice.value)) {
                    formik.setFieldValue(key, choice.value, false)
                  } else {
                    formik.setFieldValue(key, '', false)
                  }
                })
              } else {
                formik.handleChange(e)
              }
            }}
            onBlur={formik.handleBlur}
            error={
              formik.errors[`input_${field.id}`] !== undefined &&
              formik.touched[`input_${field.id}`] !== undefined
            }
          />
        )
      })}

      <div className="col-span-2 flex items-center md:col-span-4 lg:col-span-9">
        <FormSubmit isSubmitting={formik.isSubmitting}>
          <ButtonMask className="z-10 after:text-white dark:after:text-dark">
            <Typography variant="button">{submit}</Typography>
          </ButtonMask>
        </FormSubmit>
      </div>

      {statusMessage !== '' ? (
        <div
          className={cn([
            'col-span-2 md:col-span-4 lg:col-span-9',
            'font-helvetica text-[1.2rem] leading-[120%] tracking-[0.036rem]',
            {
              'text-dark': formik.status !== 'error',
              'text-red-500': formik.status === 'error'
            }
          ])}
        >
          {parse(statusMessage)}
        </div>
      ) : null}
    </form>
  )
}

export default Form
